import React, { useState, useEffect } from "react";
import { useBusinessPlan } from "../../App";
import { useCurrency } from "../../contexts/CurrencyContext";
import axios from "axios";
import Cookies from "js-cookie";
import { Tooltip as InfoTooltip } from "../ui/Tooltip";
import {
  TrendingDown,
  DollarSign,
  AlertCircle,
  CheckCircle,
  XCircle,
  Layers,
  Users,
  FileText
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  PieChart,
  Pie,
  BarChart, 
  Bar,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  XAxis, 
  YAxis, 
  ZAxis,
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const CostAnalysis = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [activeTab, setActiveTab] = useState("category");
  
  // Data states
  const [categoryData, setCategoryData] = useState(null);
  const [departmentData, setDepartmentData] = useState(null);
  const [typeData, setTypeData] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentPlan?.id) {
      fetchAllData();
    }
  }, [currentPlan]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = Cookies.get("enakid_token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      };
      
      const [categoryRes, departmentRes, typeRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/actual-data/cost-analysis/${currentPlan.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/cost-by-department/${currentPlan.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/cost-by-type/${currentPlan.id}`, { headers })
      ]);
      
      setCategoryData(categoryRes.data);
      setDepartmentData(departmentRes.data);
      setTypeData(typeRes.data);
    } catch (err) {
      console.error("Error fetching cost analysis:", err);
      setError(err.response?.data?.detail || "Failed to load cost analysis");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return formatCurrencyContext(value || 0, currentPlan?.currency);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      under_budget: { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "Under Budget" },
      on_budget: { color: "bg-blue-100 text-blue-800", icon: CheckCircle, label: "On Budget" },
      over_budget: { color: "bg-red-100 text-red-800", icon: AlertCircle, label: "Over Budget" }
    };
    
    const config = statusConfig[status] || statusConfig.on_budget;
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading Cost Analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 text-red-600">
                <AlertCircle className="w-6 h-6" />
                <p className="font-medium">{error}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!categoryData?.category_analysis?.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardContent className="p-12 text-center">
              <Layers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Cost Data Available</h3>
              <p className="text-gray-600 mb-6">
                Start recording cost actuals to see detailed cost analysis.
              </p>
              <button
                onClick={() => window.location.href = "/cost-actuals-entry"}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Record Cost Actuals
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <InfoTooltip content="Cost Analysis - Deep-dive into spending by Category, Department, and Type. Use to: Identify overspending areas, Find cost-saving opportunities, Maintain budget control. Cost variance: Negative (green) = Under budget (saved money!), Positive (red) = Over budget (investigate).">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <TrendingDown className="w-8 h-8 text-red-600" />
                Cost Analysis
              </h1>
            </InfoTooltip>
            <p className="text-gray-600 mt-2">
              Comprehensive cost performance breakdown and budget insights
            </p>
          </div>
          <InfoTooltip content="Refresh latest cost actuals and recalculate variances. Click after recording new costs or before meetings.">
            <button
              onClick={fetchAllData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </InfoTooltip>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <InfoTooltip content="Sum of all budget limits from Cost Management. Your authorized spending ceiling for the period.">
                <CardTitle className="text-sm font-medium text-gray-600">Total Budgeted</CardTitle>
              </InfoTooltip>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(categoryData.summary.total_budgeted)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <InfoTooltip content="Sum of all actual spending from Cost Actuals. Real money spent this period.">
                <CardTitle className="text-sm font-medium text-gray-600">Total Actual</CardTitle>
              </InfoTooltip>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(categoryData.summary.total_actual)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <InfoTooltip content="Cost Variance: (Actual - Budget) / Budget × 100. Negative (green) = Under budget (saved money!). Positive (red) = Over budget (overspent). Target: ≤0%. >5% = investigate immediately.">
                <CardTitle className="text-sm font-medium text-gray-600">Variance</CardTitle>
              </InfoTooltip>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${categoryData.summary.total_variance_pct <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {categoryData.summary.total_variance_pct > 0 ? "+" : ""}{categoryData.summary.total_variance_pct.toFixed(1)}%
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {formatCurrency(categoryData.summary.total_variance_amount)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <InfoTooltip content="Number of cost categories tracked and total items within them. Example: 5 categories with 23 items.">
                <CardTitle className="text-sm font-medium text-gray-600">Categories</CardTitle>
              </InfoTooltip>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">{categoryData.summary.category_count}</p>
              <p className="text-sm text-gray-600 mt-1">{categoryData.summary.total_items} items</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <InfoTooltip content="Breakdown by category (Fixed, Variable, OpEx, COGS, CapEx). Identify which types drive spending and variances.">
              <TabsTrigger value="category">By Category</TabsTrigger>
            </InfoTooltip>
            <InfoTooltip content="Breakdown by department/business unit. Track departmental budget accountability and identify over/under budget teams.">
              <TabsTrigger value="department">By Department</TabsTrigger>
            </InfoTooltip>
            <InfoTooltip content="Breakdown by type: Fixed (unchanging), Variable (scales with volume), One-time (non-recurring), Recurring (subscriptions). Understand cost structure flexibility.">
              <TabsTrigger value="type">By Cost Type</TabsTrigger>
            </InfoTooltip>
          </TabsList>

          {/* Tab 1: By Category */}
          <TabsContent value="category" className="space-y-6">
            {/* Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Donut Chart */}
              <Card>
                <CardHeader>
                  <InfoTooltip content="Donut chart: % of actual spending per category. Larger slices = Bigger cost drivers requiring close monitoring.">
                    <CardTitle>Cost Distribution by Category</CardTitle>
                  </InfoTooltip>
                  <CardDescription>Breakdown of actual costs</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={categoryData.donut_data}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={(entry) => `${entry.category}: ${entry.percentage}%`}
                      >
                        {categoryData.donut_data.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Variance Bridge */}
              <Card>
                <CardHeader>
                  <InfoTooltip content="Variance waterfall from budget to actual by category. Green bars = Under budget (savings). Red bars = Over budget. Focus on largest red bars.">
                    <CardTitle>Variance Bridge</CardTitle>
                  </InfoTooltip>
                  <CardDescription>How actual costs differ from budget</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={categoryData.variance_bridge}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" angle={-45} textAnchor="end" height={80} />
                      <YAxis tickFormatter={(value) => formatCurrency(value)} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="value" name="Cost">
                        {categoryData.variance_bridge.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.type === 'total' ? '#3b82f6' : entry.type === 'positive' ? '#ef4444' : '#10b981'} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Bullet Chart */}
            <Card>
              <CardHeader>
                <InfoTooltip content="Budget vs actual vs 110% alert limit. Blue = Budget. Red = Actual. Yellow = Alert at 110%. Red bar exceeding yellow = critical!">
                  <CardTitle>Budget vs Actual vs Limit</CardTitle>
                </InfoTooltip>
                <CardDescription>Performance against budget limits (110% threshold)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={categoryData.bullet_data} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} />
                    <YAxis dataKey="category" type="category" width={120} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="budgeted" fill="#93c5fd" name="Budget" />
                    <Bar dataKey="actual" fill="#ef4444" name="Actual" />
                    <Bar dataKey="limit" fill="#fbbf24" name="Limit (110%)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Category Table */}
            <Card>
              <CardHeader>
                <InfoTooltip content="Category breakdown: Budget, Actual, Variance ($), %, Items, Status. Green = Under budget. Red = Over budget. Status: Green (<85%), Yellow (85-100%), Red (>100%).">
                  <CardTitle>Category Performance Details</CardTitle>
                </InfoTooltip>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <InfoTooltip content="Cost category name" showIcon={false}>
                          <th className="text-left p-3 font-semibold">Category</th>
                        </InfoTooltip>
                        <InfoTooltip content="Budgeted amount (spending limit)" showIcon={false}>
                          <th className="text-right p-3 font-semibold">Budget</th>
                        </InfoTooltip>
                        <InfoTooltip content="Actual amount spent" showIcon={false}>
                          <th className="text-right p-3 font-semibold">Actual</th>
                        </InfoTooltip>
                        <InfoTooltip content="Variance: Actual - Budget. Negative = savings, Positive = overspend" showIcon={false}>
                          <th className="text-right p-3 font-semibold">Variance</th>
                        </InfoTooltip>
                        <InfoTooltip content="Variance %: (Actual - Budget) / Budget × 100" showIcon={false}>
                          <th className="text-right p-3 font-semibold">%</th>
                        </InfoTooltip>
                        <InfoTooltip content="Number of cost items in category" showIcon={false}>
                          <th className="text-center p-3 font-semibold">Items</th>
                        </InfoTooltip>
                        <InfoTooltip content="Status: Under Budget (green), At Risk (yellow), Over Budget (red)" showIcon={false}>
                          <th className="text-center p-3 font-semibold">Status</th>
                        </InfoTooltip>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryData.category_analysis.map((category, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{category.category_name}</td>
                          <td className="p-3 text-right">{formatCurrency(category.budgeted)}</td>
                          <td className="p-3 text-right font-medium">{formatCurrency(category.actual)}</td>
                          <td className="p-3 text-right">
                            <span className={category.variance_amount <= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatCurrency(category.variance_amount)}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <span className={`font-bold ${category.variance_pct <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {category.variance_pct > 0 ? "+" : ""}{category.variance_pct.toFixed(1)}%
                            </span>
                          </td>
                          <td className="p-3 text-center">{category.item_count}</td>
                          <td className="p-3 text-center">{getStatusBadge(category.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 2: By Department */}
          <TabsContent value="department" className="space-y-6">
            {/* Horizontal Bar Chart */}
            <Card>
              <CardHeader>
                <InfoTooltip content="Department Cost Comparison - Horizontal bar chart showing budgeted (blue bars) vs actual (red bars) spending for each department/business unit. How to read: Each row = one department. BLUE bar = budgeted amount (what you planned), RED bar = actual spending (what was spent). If RED bar is SHORTER than BLUE = under budget (good!). If RED bar is LONGER than BLUE = over budget (investigate!). Example: Engineering dept, Blue bar at $80K (budgeted), Red bar at $75K (actual) = under budget by $5K (good cost control). Sales dept, Blue bar at $50K, Red bar at $55K = over budget by $5K (overspending - why?). Use this for: 1) Departmental accountability - which teams control costs well, 2) Budget reallocation - move budget from under-spending to over-spending depts, 3) Headcount decisions - over-budget depts may need resource constraints. Action: Departments consistently over budget = meet with dept heads, review spending, set stricter approval processes. Departments consistently under budget = check if underfunded or underutilized.">
                  <CardTitle>Department Cost Comparison</CardTitle>
                </InfoTooltip>
                <CardDescription>Actual spending by department</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={departmentData.department_analysis} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} />
                    <YAxis dataKey="department" type="category" width={150} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="budgeted" fill="#93c5fd" name="Budgeted" />
                    <Bar dataKey="actual" fill="#ef4444" name="Actual" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Department Table */}
            <Card>
              <CardHeader>
                <CardTitle>Department Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {departmentData.department_analysis.map((dept, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <h4 className="font-bold text-lg">{dept.department}</h4>
                          <p className="text-sm text-gray-600">Owner: {dept.owner || 'Unassigned'}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold text-lg ${dept.variance_pct <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {dept.variance_pct > 0 ? "+" : ""}{dept.variance_pct.toFixed(1)}%
                          </p>
                          <p className="text-xs text-gray-600">Variance</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-600">Budgeted</p>
                          <p className="font-semibold">{formatCurrency(dept.budgeted)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Actual</p>
                          <p className="font-semibold">{formatCurrency(dept.actual)}</p>
                        </div>
                      </div>

                      <div className="border-t pt-2">
                        <p className="text-xs font-medium text-gray-700 mb-2">Cost by Category:</p>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(dept.categories).map(([category, data], cIdx) => (
                            <div key={cIdx} className="bg-gray-50 p-2 rounded text-sm">
                              <span className="font-medium">{category.replace('_', ' ').toUpperCase()}:</span>
                              <span className="ml-2">{formatCurrency(data.actual)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: By Cost Type */}
          <TabsContent value="type" className="space-y-6">
            {/* Stacked Area Chart */}
            <Card>
              <CardHeader>
                <InfoTooltip content="Cost Types Over Time - Stacked area chart showing monthly trends for each cost type (Fixed, Variable, One-time, Recurring). Each colored layer = one cost type stacked on top of each other. Total height = total actual costs for that month. How to read: (1) Bottom layer (usually Fixed costs) = baseline spending that doesn't change month-to-month, (2) Variable costs layer grows/shrinks with business activity, (3) One-time spikes = equipment purchases or one-off expenses, (4) Recurring layer = subscriptions and licenses. Use cases: Track cost structure changes over time, Identify unexpected spikes (investigate causes), Spot seasonal patterns (e.g., higher variable costs in peak sales months), Monitor if fixed costs are growing (may indicate loss of flexibility). Example pattern: If variable costs shrink while fixed costs grow, you're losing operational flexibility - harder to scale down in tough times. Action: Aim to keep fixed costs <60% of total for flexibility.">
                  <CardTitle>Cost Types Over Time</CardTitle>
                </InfoTooltip>
                <CardDescription>Monthly breakdown of cost types</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={typeData.time_series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    {typeData.type_names.map((typeName, index) => (
                      <Area
                        key={typeName}
                        type="monotone"
                        dataKey={`${typeName}_actual`}
                        stackId="1"
                        stroke={COLORS[index % COLORS.length]}
                        fill={COLORS[index % COLORS.length]}
                        name={typeName}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Scatter Plot - Cost Efficiency */}
            <Card>
              <CardHeader>
                <InfoTooltip content="Cost Efficiency Analysis - Scatter plot where each dot = one cost type plotted by Budgeted (X-axis) vs Actual (Y-axis) spending. Diagonal line = perfect 1:1 budget-to-actual ratio. How to interpret: Dots ON the diagonal = spent exactly as budgeted (perfect!), Dots BELOW diagonal = spent less than budget (under budget = good cost control), Dots ABOVE diagonal = spent more than budget (over budget = need investigation). Distance from diagonal = magnitude of variance. Example: Fixed costs at ($100K budgeted, $95K actual) = below line = 5% savings. Variable costs at ($50K budgeted, $60K actual) = above line = 20% overspend. Use this to: (1) Quickly identify which cost types have biggest over/underspend, (2) Spot outliers that need immediate attention (far from diagonal), (3) Compare efficiency across cost types. Pattern insights: All dots below diagonal = excellent overall cost control. Multiple dots far above diagonal = systemic cost management issues. Action: Focus on dots furthest above diagonal first - highest priority for cost reduction.">
                  <CardTitle>Cost Efficiency Analysis</CardTitle>
                </InfoTooltip>
                <CardDescription>Budgeted vs Actual spending patterns</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      type="number" 
                      dataKey="budgeted" 
                      name="Budgeted"
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <YAxis 
                      type="number" 
                      dataKey="actual" 
                      name="Actual"
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <ZAxis range={[100, 400]} />
                    <Tooltip 
                      cursor={{ strokeDasharray: '3 3' }}
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Legend />
                    <Scatter 
                      name="Cost Types" 
                      data={typeData.type_analysis} 
                      fill="#3b82f6"
                    >
                      {typeData.type_analysis.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Cost Type Table */}
            <Card>
              <CardHeader>
                <InfoTooltip content="Cost Type Breakdown Table - Detailed line-by-line analysis of spending by cost type (Fixed, Variable, One-time, Recurring). Shows Budget (planned), Actual (spent), Variance (difference), and % (variance percentage). Understanding cost types is critical for cost structure optimization: (1) FIXED COSTS (salaries, rent, insurance) = Don't change with volume, provide stability but reduce flexibility. Target: <60% of total costs. Over-budgeting = wasted capital, Under-budgeting = operational disruptions. (2) VARIABLE COSTS (materials, commissions, shipping) = Scale with revenue/production. Target: Match revenue growth rate. Over-budget = inefficiency or volume growth, Under-budget = lower activity or good negotiations. (3) ONE-TIME COSTS (equipment, setup fees, consulting projects) = Non-recurring expenses. Over-budget = scope creep or poor estimation, Should be $0 most months unless planned investments. (4) RECURRING COSTS (subscriptions, licenses, maintenance) = Regular predictable expenses. Target: <15% of total costs. Over-budget = subscription bloat (audit and cancel unused), Under-budget = potential service gaps. How to optimize your cost structure: High fixed + low variable = leverage (great when growing, risky when shrinking), High variable + low fixed = flexibility (safer but less leverage), Too many one-time costs = unpredictable cash flow (consolidate and plan), High recurring without ROI = subscription waste (audit quarterly). Example interpretation: If Variable costs -$10K under budget but revenue also down 20%, that's actually GOOD cost control (variable costs scaled appropriately). If Fixed costs +$15K over budget, that's CONCERNING (fixed costs shouldn't vary - investigate unauthorized hiring or lease changes).">
                  <CardTitle>Cost Type Breakdown</CardTitle>
                </InfoTooltip>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <InfoTooltip content="Cost Type: Fixed (unchanging like rent), Variable (scales with volume like materials), One-time (non-recurring like equipment), Recurring (subscriptions). Understanding your cost type mix is fundamental to financial flexibility and scalability." showIcon={false}>
                          <th className="text-left p-3 font-semibold">Cost Type</th>
                        </InfoTooltip>
                        <InfoTooltip content="Budgeted amount = How much you PLANNED to spend on this cost type for the period. Example: $100K budgeted for Fixed costs means you authorized $100K in salaries, rent, insurance combined." showIcon={false}>
                          <th className="text-right p-3 font-semibold">Budgeted</th>
                        </InfoTooltip>
                        <InfoTooltip content="Actual amount = How much you ACTUALLY spent on this cost type. Example: $95K actual Fixed costs means you paid $95K total across all fixed cost items (salaries, rent, insurance)." showIcon={false}>
                          <th className="text-right p-3 font-semibold">Actual</th>
                        </InfoTooltip>
                        <InfoTooltip content="Variance $ = Actual - Budget. For costs: Negative (green) = UNDER budget = saved money (GOOD!), Positive (red) = OVER budget = overspent (BAD!). Example: -$5K variance on Fixed costs = saved $5K vs plan." showIcon={false}>
                          <th className="text-right p-3 font-semibold">Variance</th>
                        </InfoTooltip>
                        <InfoTooltip content="Variance % = (Actual - Budget) / Budget × 100. Shows percentage over/under budget. Negative = under budget (saved), Positive = over budget (overspent). Example: -5% = spent 5% less than planned (good). +10% = spent 10% more (investigate why)." showIcon={false}>
                          <th className="text-right p-3 font-semibold">%</th>
                        </InfoTooltip>
                      </tr>
                    </thead>
                    <tbody>
                      {typeData.type_analysis.map((type, index) => {
                        const variance_amount = type.actual - type.budgeted;
                        return (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{type.cost_type}</td>
                            <td className="p-3 text-right">{formatCurrency(type.budgeted)}</td>
                            <td className="p-3 text-right font-medium">{formatCurrency(type.actual)}</td>
                            <td className="p-3 text-right">
                              <span className={variance_amount <= 0 ? 'text-green-600' : 'text-red-600'}>
                                {formatCurrency(variance_amount)}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              <span className={`font-bold ${type.variance_pct <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {type.variance_pct > 0 ? "+" : ""}{type.variance_pct.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CostAnalysis;
